import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { NavigateFunction } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  toggleResearchAssistant,
  useIsResearchAssistantEnabled,
  useIsResearchAssistantOpen,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import {
  getCohortsPath,
  getOrganizationsBasePath,
  getProjectsBasePath,
  getUsersBasePath,
  getConceptGroupsBasePath,
  getAnalyticalVariablesBasePath,
  // TODO: cleanup with data_reliability ff
  getDataReliabilityAccuracyPath,
  // TODO: cleanup with research_assistant_developer_tools ff
  getRaSessionReviewBasePath,
} from '@verantos-dev/core-constants';
import {
  EvidencePlatformSidebarAppID,
  OrganizationsSidebarAppFeatureId,
  ProjectsSidebarAppFeatureId,
  UserSidebarAppFeatureId,
} from '@verantos-dev/core-enums';
import { useSafeRouteParams } from '@verantos-dev/data-access-cohorts';
import { Icon, type IconType } from '@verantos-dev/shared-components';
import type {
  SidebarConfig,
  SidebarAdditionalItem,
  SidebarContext,
} from '@verantos-dev/shared-layout';
import { colors } from '@verantos-dev/shared-ui-components/tokens';

/**
 * VEP-specific sidebar configuration hook.
 * Provides all the VEP-specific mappings for icons, URLs, handlers, and patterns.
 */
export const useVepSidebarConfig = (): SidebarConfig => {
  const intl = useIntl();
  const {
    user: { isInternalUser },
  } = useAuth();
  let { projectId } = useSafeRouteParams();

  // We are fetching projectId from the URL if it's not available through react router params
  // This is VEP-specific logic for extracting projectId from the URL
  if (!projectId) {
    const path = window.location.pathname;
    const segments = path.replace(/\/+$/, '').split('/').filter(Boolean);
    const projectsIndex = segments.indexOf('projects');
    projectId = projectsIndex !== -1 ? segments[projectsIndex + 1] : '';
  }

  const isResearchAssistantEnabled = useIsResearchAssistantEnabled();
  const isResearchAssistantOpen = useIsResearchAssistantOpen();
  // TODO: cleanup with research_assistant_gen_3 ff
  // gen_3 (agent-first) embeds the Research Assistant directly in the project
  // page, so it is no longer a sidebar nav destination — drop the sidebar item.
  const isAgentFirstEnabled = useFeatureFlagSplit('research_assistant_gen_3', false);
  // TODO: cleanup with data_reliability ff
  const isDataReliabilityPageEnabled = useFeatureFlagSplit('data_reliability', false);
  // TODO: cleanup with research_assistant_developer_tools ff — OR-6432 Block B.
  // Divider placement needs isInternalUser ANDed in here too (not just in
  // useAllowedApps.ts): if only the flag were checked, a non-internal user
  // with the flag globally ON would lose the group divider after Data
  // Reliability even though the RA Session Review item never actually
  // renders for them (useAllowedApps.ts filters it out by role/isInternalUser
  // before this hook's allowedApps prop is built). Hook called unconditionally
  // (rules of hooks); isInternalUser is ANDed in after the call.
  const isRaDeveloperToolsFlagEnabled = useFeatureFlagSplit(
    'research_assistant_developer_tools',
    false
  );
  const isRaSessionReviewEnabled = isInternalUser && isRaDeveloperToolsFlagEnabled;

  return useMemo(() => {
    const getIcon = (
      appId:
        | EvidencePlatformSidebarAppID
        | UserSidebarAppFeatureId
        | ProjectsSidebarAppFeatureId
        | OrganizationsSidebarAppFeatureId
        | string
    ): 'noIcon' | ('blank' | IconType) => {
      switch (appId) {
        case EvidencePlatformSidebarAppID.projects:
          return 'sidebarStudies';
        case EvidencePlatformSidebarAppID.users:
          return 'sidebarUsers';
        case EvidencePlatformSidebarAppID.organizations:
          return 'sidebarOrganization';
        case EvidencePlatformSidebarAppID.extractionAccuracy:
          return 'sidebarExtractionAccuracy';
        case EvidencePlatformSidebarAppID.conceptGroups:
          return 'sidebarConceptGroups';
        case EvidencePlatformSidebarAppID.analyticalVariables:
          return 'functions';
        // TODO: cleanup with data_reliability ff
        case EvidencePlatformSidebarAppID.dataReliability:
          return 'sidebarAccuracy';
        // TODO: cleanup with research_assistant_developer_tools ff — no dedicated
        // icon exists for this app yet; reusing sidebarDataAdmin as the closest
        // existing "internal tooling" icon rather than inventing a new asset.
        // Flag for design input (Barry Richardson) if this page graduates
        // past the spike.
        case EvidencePlatformSidebarAppID.raSessionReview:
          return 'sidebarDataAdmin';
        case ProjectsSidebarAppFeatureId.cohorts:
          return 'sidebarCohorts';
        default:
          return 'noIcon';
      }
    };

    const getUrl = (
      appId:
        | EvidencePlatformSidebarAppID
        | UserSidebarAppFeatureId
        | ProjectsSidebarAppFeatureId
        | OrganizationsSidebarAppFeatureId
        | string,
      context: SidebarContext
    ): string => {
      const projectId = typeof context.projectId === 'string' ? context.projectId : undefined;
      switch (appId) {
        case EvidencePlatformSidebarAppID.projects:
          return getProjectsBasePath();
        case EvidencePlatformSidebarAppID.users:
          return getUsersBasePath();
        case EvidencePlatformSidebarAppID.organizations:
          return getOrganizationsBasePath();
        case EvidencePlatformSidebarAppID.conceptGroups:
          return getConceptGroupsBasePath();
        case EvidencePlatformSidebarAppID.analyticalVariables:
          return getAnalyticalVariablesBasePath();
        // TODO: cleanup with data_reliability ff
        case EvidencePlatformSidebarAppID.dataReliability:
          return getDataReliabilityAccuracyPath();
        // TODO: cleanup with research_assistant_developer_tools ff
        case EvidencePlatformSidebarAppID.raSessionReview:
          return getRaSessionReviewBasePath();
        case ProjectsSidebarAppFeatureId.cohorts:
          return getCohortsPath(projectId || '');
        default:
          return '/';
      }
    };

    const getClickHandler = (
      appId:
        | EvidencePlatformSidebarAppID
        | UserSidebarAppFeatureId
        | ProjectsSidebarAppFeatureId
        | OrganizationsSidebarAppFeatureId
        | string,
      navigateFn: NavigateFunction,
      context: SidebarContext
    ) => {
      const cleanupCohortState = () => {
        // dispatch(resetCohortBuilderSlice());
        // dispatch(resetResearchAssistantMessages());
      };
      const cleanupStudyState = () => {
        cleanupCohortState();
        // dispatch(resetCurrentState());
      };

      switch (appId) {
        case EvidencePlatformSidebarAppID.projects:
        case EvidencePlatformSidebarAppID.conceptGroups:
        case EvidencePlatformSidebarAppID.analyticalVariables:
        // TODO: cleanup with data_reliability ff
        case EvidencePlatformSidebarAppID.dataReliability:
          return () => {
            cleanupStudyState();
            navigateFn(getUrl(appId, context));
          };
        case EvidencePlatformSidebarAppID.users:
        case EvidencePlatformSidebarAppID.organizations:
        case EvidencePlatformSidebarAppID.extractionAccuracy:
        // TODO: cleanup with research_assistant_developer_tools ff — no
        // cohort/study state to reset here (this page isn't project-scoped),
        // so it joins the plain-navigate group rather than the
        // cleanupStudyState group above.
        case EvidencePlatformSidebarAppID.raSessionReview:
          return () => {
            navigateFn(getUrl(appId, context));
          };
        case ProjectsSidebarAppFeatureId.cohorts:
          return () => {
            cleanupCohortState();
            navigateFn(getUrl(appId, context));
          };
        default:
          return () => {
            navigateFn('/');
          };
      }
    };

    const getMatchPatterns = (
      appId:
        | EvidencePlatformSidebarAppID
        | UserSidebarAppFeatureId
        | ProjectsSidebarAppFeatureId
        | OrganizationsSidebarAppFeatureId
        | string
    ): string[] => {
      switch (appId) {
        case EvidencePlatformSidebarAppID.projects:
        case EvidencePlatformSidebarAppID.users:
        case EvidencePlatformSidebarAppID.conceptGroups:
        case EvidencePlatformSidebarAppID.analyticalVariables:
        // TODO: cleanup with research_assistant_developer_tools ff
        case EvidencePlatformSidebarAppID.raSessionReview:
          return [appId];
        case ProjectsSidebarAppFeatureId.cohorts:
          return [appId, 'cohort-builder'];
        case EvidencePlatformSidebarAppID.organizations:
          return [appId, 'customers', 'research-partners', 'verantos'];
        // TODO: cleanup with data_reliability ff
        case EvidencePlatformSidebarAppID.dataReliability:
          return [appId, 'accuracy', 'completeness', 'traceability'];
        default:
          return ['/'];
      }
    };

    const textColor = isResearchAssistantOpen && isResearchAssistantEnabled ? 'primary' : undefined;
    const iconColor = isResearchAssistantOpen && isResearchAssistantEnabled ? 'primary' : 'black';

    const researchAssistantItem: SidebarAdditionalItem = {
      position: 1, // After toggle button (position 0)
      getItem: () => ({
        id: 'research-assistant',
        icon: 'researchAssistantIcon',
        label: intl.formatMessage({ id: 'sidebar.researchAssistant' }),
        selected: isResearchAssistantOpen,
        disabled: !isResearchAssistantEnabled,
        isSelectable: true,
        onClick: () => toggleResearchAssistant(),
        divider: true,
        tooltip: !isResearchAssistantEnabled
          ? intl.formatMessage({ id: 'sidebar.researchAssistant.tooltip.disabled' })
          : undefined,
        isNotPageSelection: true,
        customTextColor: textColor,
        customIconColor: iconColor,
        showBadge: true,
        badgeContent: <Icon id="researchAssistantIconSparkles" icon="aiSparkles" />,
        badgeProps: {
          sx: {
            '& .MuiBadge-badge': {
              transform: 'translate(50%, -50%) translateY(4px)',
              backgroundColor: 'transparent',
              color: 'inherit',
            },
          },
        },
      }),
    };

    const getContext = () => ({
      projectId,
    });

    return {
      getIcon,
      getUrl,
      getClickHandler,
      getMatchPatterns,
      getContext,
      defaultIconColor: colors['neutral-900'],
      dividerConfig: {
        // For projects page, don't add dividers between Projects and its sub-pages
        noDividerForActiveSubItems: [EvidencePlatformSidebarAppID.projects],
        // Projects, Analytical Variables, Concept Groups, and (when enabled) Data
        // Reliability / RA Session Review are visually grouped together; the divider
        // falls after the last of the group, separating them from the sys-admin
        // items (Users, Organizations). Each flag that adds a new last-of-group
        // item must suppress the divider on the item that was previously last.
        // TODO: cleanup with data_reliability ff — make conceptGroups unconditional here.
        // TODO: cleanup with research_assistant_developer_tools ff — make
        // dataReliability's suppression unconditional here (folded into the
        // data_reliability cleanup above once both flags are gone).
        noDividerForSingleItems: [
          EvidencePlatformSidebarAppID.projects,
          EvidencePlatformSidebarAppID.analyticalVariables,
          ...(isDataReliabilityPageEnabled || isRaSessionReviewEnabled
            ? [EvidencePlatformSidebarAppID.conceptGroups]
            : []),
          ...(isDataReliabilityPageEnabled && isRaSessionReviewEnabled
            ? [EvidencePlatformSidebarAppID.dataReliability]
            : []),
        ],
      },
      // TODO: cleanup with research_assistant_gen_3 ff — RA is embedded in the
      // page under gen_3, so the sidebar RA item is dropped.
      additionalItems: isAgentFirstEnabled ? [] : [researchAssistantItem],
    };
  }, [
    intl,
    isAgentFirstEnabled,
    isDataReliabilityPageEnabled,
    isRaSessionReviewEnabled,
    isResearchAssistantEnabled,
    isResearchAssistantOpen,
    projectId,
  ]);
};
